import { useState, type ReactNode } from 'react';

interface Props {
  /** What the tooltip is anchored to (e.g. a KPI label). */
  children: ReactNode;
  /** Multi-line explanation. Use \n for line breaks; ** for bold. */
  text: string;
}

/**
 * Tiny hover/click info tooltip. Renders an "i" icon next to its children;
 * the explanation pops up below on hover or focus. Mobile-friendly: clicking
 * the icon toggles the popover so touch users can read it.
 *
 * Markdown-lite: lines wrapped in **...** render bold. Newlines split paragraphs.
 */
export default function InfoTooltip({ children, text }: Props) {
  const [open, setOpen] = useState(false);

  const blocks = text.split('\n').map((line, i) => {
    const parts = line.split(/\*\*(.+?)\*\*/g);
    return (
      <p key={i} className={line.trim() === '' ? 'h-2' : 'mb-1 last:mb-0'}>
        {parts.map((p, j) =>
          j % 2 === 1 ? <strong key={j} className="font-semibold">{p}</strong> : <span key={j}>{p}</span>,
        )}
      </p>
    );
  });

  return (
    <span className="relative inline-flex items-center gap-1">
      {children}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        aria-label="Más información"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-tremor-content/40 text-xs leading-none text-tremor-content/70 hover:bg-tremor-content/10 hover:text-tremor-content-strong focus:outline-none focus:ring-2 focus:ring-tremor-brand"
      >
        i
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-0 top-full z-20 mt-2 w-72 rounded-tremor-default bg-gray-900 dark:bg-gray-700 p-3 text-xs leading-snug text-white shadow-lg"
        >
          {blocks}
        </span>
      )}
    </span>
  );
}
